import { useEffect, useRef, useState } from "react";
const LERP_FACTOR = 0.04;
const EPSILON = 0.1;

/**
 * Smoothly interpolates toward a target progress value using a
 * requestAnimationFrame lerp loop.  When `isActive` is false the
 * returned value snaps to `target` immediately (no animation).
 *
 * The returned value is guaranteed to be monotonically non-decreasing:
 * it will never visually jump backward even if `target` drops (e.g. when
 * a late-finalizing boost is removed and the raw progress is lower).
 */
export function useSmoothedProgress(target: number, isActive: boolean): number {
  const [rendered, setRendered] = useState(0);
  const renderedRef = useRef(0);
  const targetRef = useRef(0);
  useEffect(() => {
    targetRef.current = target;
  }, [target]);
  useEffect(() => {
    if (isActive) {
      return;
    }
    const snapped = Math.max(renderedRef.current, target);
    renderedRef.current = snapped;
    setRendered(snapped);
  }, [target, isActive]);
  useEffect(() => {
    if (!isActive) {
      return;
    }
    let raf = 0;
    const tick = () => {
      const current = renderedRef.current;
      const delta = targetRef.current - current;
      if (Math.abs(delta) <= EPSILON) {
        if (current !== targetRef.current) {
          const clamped = Math.max(current, targetRef.current);
          renderedRef.current = clamped;
          setRendered(clamped);
        }
        return;
      }
      const next = Math.max(current, current + delta * LERP_FACTOR);
      renderedRef.current = next;
      setRendered(next);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, isActive]);
  if (renderedRef.current === 0 && target > 0) {
    return target;
  }
  return rendered;
}